import {specialBodiesSlices, entities} from '../../util/uiconstants';
import {normalize, truthyWinsMergeStrategy} from '../../util/utils';
import {flatten} from 'lodash';

export function normalizeScots(data, state = {}) {
	const scots = data.map((scot) => ({
		...scot,
		itApplications: scot.itApplications?.map((itApplication) => itApplication.id),
		controls: scot.controls?.map((control) => control.id)
	}));

	const controls = data
		.flatMap((scot) => scot.controls || [])
		.map((control) => {
			const relatedScots = scots
				.filter((scot) => scot.controls?.some((id) => id === control.id))
				.map((scot) => scot.id);

			return {
				...control,
				itApplications: control.itApplications?.map((itApplication) => itApplication.id) || [],
				scots: relatedScots || []
			};
		});

	return {
		...state,
		scots: normalize(scots, undefined, state.scots, truthyWinsMergeStrategy),
		itApplications: normalize(
			flatten(
				data
					// Scots do have itApplications related
					.map((scot) => scot.itApplications)
					// Controls do have itApplications related, but duplicated entries are going to be taken care of by the normalize function
					.concat(flatten(data.map((scot) => scot.controls.map((control) => control.itApplications))))
			),
			'id',
			state.itApplications,
			truthyWinsMergeStrategy
		),
		controls: normalize(controls, 'id', state.controls, truthyWinsMergeStrategy)
	};
}

export function normalizeItApplications(data, state = {}) {
	const controls = normalize(
		flatten(data.map((itApplication) => itApplication?.controls || [])),
		undefined,
		state.controls,
		truthyWinsMergeStrategy
	);
	const scots = data
		.flatMap((itApplication) => itApplication.scots || [])
		.map((scot) => {
			const controls = state?.scots
				? [...(state?.scots?.byId[scot.scotId]?.controls || []), ...(scot.scotControls || [])]
				: [...(scot.scotControls || [])];

			const _scot = {
				...scot,
				controls: [...new Set(controls)]
			};
			delete _scot.scotControls;
			return _scot;
		});

	return {
		...state,
		itApplications: normalize(
			data.map((itApplication) => ({
				...itApplication,
				scots: itApplication.scots?.map((scot) => scot.scotId),
				controls: itApplication.controls?.map((control) => control.id),
				itProcesses: itApplication.itProcesses?.map((itProcess) => itProcess.id),
				selected: false,
				hasAssociation: false
			})),
			undefined,
			state.itApplications,
			truthyWinsMergeStrategy
		),
		scots: normalize(scots, 'scotId', state.scots, truthyWinsMergeStrategy),
		controls: controls,
		itProcesses: normalize(
			flatten(data.map((itApplication) => itApplication.itProcesses)),
			undefined,
			state.itProcesses,
			truthyWinsMergeStrategy
		)
	};
}

export function normalizeItProcesses(data, state = {}) {
	// IT Risks and ITControls are comming denormalized in the response. This is for nesting them accordingly (itRisk -> itControl)
	const itProcessesList = data.map((itProcess) => {
		itProcess.itRisks = buildItRisksForItProcess(itProcess);

		return itProcess;
	});

	const allItRisks = flatten(itProcessesList.map((itProcess) => itProcess.itRisks));
	const allItControls = flatten(itProcessesList.map((itProcess) => itProcess.itControls));
	const allItApplications = flatten(itProcessesList.map((itProcess) => itProcess.itApplications));

	const itProcesses = normalize(
		itProcessesList.map((itProcess) => ({
			...itProcess,
			itApplications: itProcess.itApplications?.map((itApplication) => itApplication.id),
			relatedTasks: itProcess.relatedTasks?.map((relatedTask) => relatedTask.id),
			controls: itProcess.controls?.map((control) => control.id),
			itRisks: itProcess.itRisks?.map((itRisk) => itRisk.id),
			itControls: itProcess.itControls?.map((itControl) => itControl.id),
			selected: false,
			hasAssociation: false
		})),
		undefined,
		state.itProcesses,
		truthyWinsMergeStrategy
	);

	return {
		...state,
		itProcesses,
		itApplications: normalize(allItApplications, undefined, state.itApplications, truthyWinsMergeStrategy),
		itRisks: normalize(
			allItRisks.map((itRisk) => ({
				...itRisk,
				itControls: itRisk.itControls.map((itCtrl) => itCtrl.id)
			})),
			undefined,
			state.itRisks,
			truthyWinsMergeStrategy
		),
		itControls: normalize(allItControls, undefined, state.itControls, truthyWinsMergeStrategy),
		relatedTasks: normalize(
			flatten(itProcessesList.map((itProcess) => itProcess.relatedTasks)),
			undefined,
			state.relatedTasks,
			truthyWinsMergeStrategy
		),
		controls: normalize(
			flatten(itProcessesList.map((itProcess) => itProcess.controls)),
			undefined,
			state.controls,
			truthyWinsMergeStrategy
		)
	};
}

export function normalizeItControls(data, state = {}) {
	return {
		...state,
		itControls: normalize(data ? data.data : [], undefined, state.itControls, truthyWinsMergeStrategy)
	};
}

export function normalizeRiskTypes(data, state = {}) {
	return {
		...state,
		riskTypes: normalize(data, undefined, state.riskTypes, truthyWinsMergeStrategy)
	};
}

export function normalizeRisks(data, state = {}) {
	return {
		...state,
		risks: normalize(data, undefined, state.risks, truthyWinsMergeStrategy)
	};
}

export function normalizeRiskFactors(data, state = {}, replace = false, mergeStrategy = truthyWinsMergeStrategy) {
	const _data = data?.map((riskFactor) => {
		return {
			...riskFactor,
			riskId: riskFactor.riskId || null
		};
	});
	return {
		...state,
		riskFactors: normalize(_data, undefined, state.riskFactors, mergeStrategy, replace)
	};
}

export function normalizeEntities(data, state = {}) {
	if (data.length === 0) {
		return {
			...state
		};
	}
	const sliceName = specialBodiesSlices[data.find((entity) => entity.entityId).entityId];

	const value = data.map((entity) => {
		const childEntity = entity.children?.find((child) => {
			return child.entityId;
		});

		let returnValue = {...entity, id: Number(entity.entityUId)};
		if (childEntity) {
			// Each entity may have a different set of children
			const childrenPropertyName = entity.parentEntityId
				? `${entity.parentEntityId}_${entity.parentEntityUId}_children`
				: 'children';

			// Task entity id is a string (taskId), so we need to avoid converting the id to a number (if any other entity type is a string, add to the below condition)
			const isNumericEntityUId = childEntity.entityId !== entities.Task;

			returnValue = {
				...returnValue,
				[specialBodiesSlices[childEntity.entityId]]: entity.children.map((child) =>
					isNumericEntityUId ? Number(child.entityUId) : child.entityUId
				),
				[childrenPropertyName]: entity.children.map((child) => {
					return {entityId: child.entityId, entityUId: child.entityUId};
				})
			};

			if (entity.parentEntityId) {
				delete returnValue.children;
			}
		}
		return returnValue;
	});
	let normalizedData = {
		...state,
		[sliceName]: normalize(value, undefined, state[sliceName], truthyWinsMergeStrategy)
	};
	const children = data
		.filter((entity) => entity.children?.length > 0)
		.flatMap((entity) =>
			entity.children.map((child) => {
				return {
					parentEntityId: entity.entityId,
					parentEntityUId: entity.entityUId,
					...child
				};
			})
		);
	return normalizeEntities(children, normalizedData);
}

export function normalizeControls(control, state = {}) {
	const existingControl = state.controls?.byId[control.id];
	if (!existingControl) {
		return {
			...state,
			controls: {
				...state.controls,
				byId: {
					...state.controls?.byId,
					[control.id]: control
				}
			}
		};
	}

	const mergedControl = {
		...existingControl,
		scots: existingControl?.scots?.concat(
			control.scots.filter((scot) => !existingControl.scots.some((existingScot) => existingScot.id === scot.id))
		)
	};

	return {
		...state,
		controls: {
			...state.controls,
			byId: {
				...state.controls.byId,
				[control.id]: mergedControl
			}
		}
	};
}

// Given a denormalized list of itRisks and itControls, returns a normalized (nested) version of the data
function buildItRisksForItProcess(itProcess) {
	const normalizedItRisks = [];
	const itProcessItRisks = itProcess?.itRisks;
	const itProcessItControls = itProcess?.itControls;

	if (itProcessItRisks && itProcessItRisks.length > 0 && itProcessItControls) {
		for (const itRisk of itProcessItRisks) {
			itRisk.itProcessName = itProcess.itProcessName;
			itRisk.itControls = itProcessItControls.filter((itCtrl) => itCtrl.itRiskId === itRisk.id);
			normalizedItRisks.push(itRisk);
		}
	}

	return normalizedItRisks;
}

const mapStrategies = {
	[entities.SCOT]: (data, state = {}) => {
		return normalizeScots(data, state);
	},
	[entities.ItSoApplication]: (data, state = {}) => {
		return state;
	},
	[entities.ItProcess]: (data, state = {}) => state,
	[entities.ItControl]: (data, state = {}) => state
};

const defaultStrategy = (_, state) => state;

/**
 * Gets the specified body type Id mapping strategy.
 * @param {number} entityId The entity id.
 * @returns {Function} A strategy function for mapping.
 */
export function getMapStrategy(entityId) {
	return mapStrategies[entityId] || defaultStrategy;
}
